import React, { useEffect, useMemo, useState } from "react";
import "./PendingVerifications.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const ALLOWED_MODULES = [2, 4, 9, 10, 15];

export default function PendingVerifications() {
  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const deptId   = parseInt(localUser?.deptid, 10);
  const empId    = parseInt(localUser?.id, 10);

  const [employees, setEmployees]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [actionMsg, setActionMsg]   = useState({ type: "", text: "" });
  const [processing, setProcessing] = useState(new Set());

  const fetchPending = async () => {
    setLoading(true);
    setError("");
    setActionMsg({ type: "", text: "" });
    try {
      const params = new URLSearchParams({ moduleId });
      if ([2, 15].includes(moduleId) && deptId) params.append("deptId", deptId);
      if (moduleId === 9 && empId)               params.append("empId", empId);

      const res  = await fetch(`${API_ROOT}/api/employees/pending-verifications?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load pending verifications");
      setEmployees(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message || "Failed to load pending verifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) return;
    fetchPending();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const handleAction = async (employeeId, status, mobile, selectEl) => {
    const label = status === "Y" ? "Approve" : "Reject";
    if (!window.confirm(`${label} employee registration?`)) {
      selectEl.value = "";
      return;
    }

    setProcessing(prev => new Set(prev).add(employeeId));
    setActionMsg({ type: "", text: "" });

    try {
      const res = await fetch(`${API_ROOT}/api/employees/pending-verifications/update-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ moduleId, employeeId, status, mobile })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update status");

      setActionMsg({ type: "success", text: data.message || "Status updated." });
      setEmployees(prev => prev.filter(e => e.EmployeeID !== employeeId));
    } catch (err) {
      setActionMsg({ type: "error", text: err.message || "Failed to update status." });
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(employeeId);
        return next;
      });
      selectEl.value = "";
    }
  };

  if (!ALLOWED_MODULES.includes(moduleId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">Pending Verification of Employees</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      {actionMsg.text && (
        <div className={`alert alert-${actionMsg.type === "success" ? "success" : "danger"}`}>
          {actionMsg.text}
        </div>
      )}

      {loading ? (
        <div className="text-muted">Loading...</div>
      ) : !error && employees.length === 0 ? (
        <div className="alert alert-info">No pending verifications found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover table-sm align-middle pending-verif-table" style={{ borderCollapse: "collapse", "--bs-table-border-color": "#000", "--bs-table-striped-bg": "#f2f2f2", "--bs-table-hover-bg": "#aed6f1" }}>
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Designation</th>
                <th>Department</th>
                <th style={{ whiteSpace: "nowrap", width: "1%" }}>Doc</th>
                <th style={{ whiteSpace: "nowrap", width: "1%" }}>Edit</th>
                <th style={{ minWidth: "160px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, idx) => (
                <tr key={emp.EmployeeID}>
                  <td>{idx + 1}</td>
                  <td>{emp.EmpName}</td>
                  <td>{emp.Mobile}</td>
                  <td>{emp.Designation}</td>
                  <td>{emp.Department}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {emp.SupportingDoc ? (
                      <a
                        href={`${API_ROOT}/api/profile/supportingdoc/${encodeURIComponent(emp.SupportingDoc)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </a>
                    ) : (
                      <span style={{ color: "red" }}>NoDoc</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <a
                      href={`/employee-edit/${emp.EmployeeID}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(`/employee-edit/${emp.EmployeeID}`, "_blank", "width=1200,height=850,scrollbars=yes,resizable=yes");
                      }}
                    >
                      Edit
                    </a>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      defaultValue=""
                      disabled={processing.has(emp.EmployeeID)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        handleAction(emp.EmployeeID, val, emp.Mobile, e.target);
                      }}
                    >
                      <option value="" disabled>Status</option>
                      <option value="Y">Approved</option>
                      <option value="R">Rejected</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
